/**
 * Log Collection System
 * 
 * Captures all console logs for debugging and viewing in the dev portal.
 * Only active in development or when explicitly enabled.
 */

export type LogLevel = 'log' | 'info' | 'warn' | 'error' | 'debug'

export interface LogEntry {
  id: string
  timestamp: Date
  level: LogLevel
  message: string
  args: any[]
  stack?: string
  url?: string
  lineNumber?: number
  columnNumber?: number
}

class LogCollector {
  private logs: LogEntry[] = []
  private maxLogs = 1000
  private enabled = false
  private listeners: Set<(logs: LogEntry[]) => void> = new Set()
  
  // Store original console methods
  private originalConsole = {
    log: console.log,
    info: console.info,
    warn: console.warn,
    error: console.error,
    debug: console.debug,
  }

  constructor() {
    // Auto-enable in development OR if explicitly enabled in localStorage
    if (import.meta.env.DEV || localStorage.getItem('dev_logs_enabled') === 'true') {
      this.enable()
    }
    
    // Also enable if the current hostname suggests dev environment
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname
      if (hostname.includes('dev') || hostname.includes('localhost') || hostname === '127.0.0.1') {
        this.enable()
      }
    }
  }

  enable() {
    if (this.enabled) return
    this.enabled = true
    
    // Intercept console methods
    console.log = this.createInterceptor('log')
    console.info = this.createInterceptor('info')
    console.warn = this.createInterceptor('warn')
    console.error = this.createInterceptor('error')
    console.debug = this.createInterceptor('debug')

    // Capture global errors
    window.addEventListener('error', this.handleGlobalError)
    window.addEventListener('unhandledrejection', this.handleUnhandledRejection)

    console.info('[LogCollector] Started capturing logs')
  }

  disable() {
    if (!this.enabled) return
    this.enabled = false
    
    // Restore original console methods
    console.log = this.originalConsole.log
    console.info = this.originalConsole.info
    console.warn = this.originalConsole.warn
    console.error = this.originalConsole.error
    console.debug = this.originalConsole.debug

    window.removeEventListener('error', this.handleGlobalError)
    window.removeEventListener('unhandledrejection', this.handleUnhandledRejection)

    this.originalConsole.info('[LogCollector] Stopped capturing logs')
  }

  private createInterceptor(level: LogLevel) {
    return (...args: any[]) => {
      // Call original console method
      this.originalConsole[level](...args)
      
      // Store log entry
      this.addLog(level, args)
    }
  }

  private addLog(level: LogLevel, args: any[]) {
    const entry: LogEntry = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date(),
      level,
      message: this.formatMessage(args),
      args,
    }

    // Add stack trace for errors
    if (level === 'error') {
      entry.stack = new Error().stack
    }

    this.logs.push(entry)
    
    // Trim logs if exceeding max
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(-this.maxLogs)
    }

    // Notify listeners
    this.notifyListeners()
  }

  private handleGlobalError = (event: ErrorEvent) => {
    this.addLog('error', [
      `Global Error: ${event.message}`,
      {
        url: event.filename,
        line: event.lineno,
        column: event.colno,
        error: event.error,
      }
    ])
  }

  private handleUnhandledRejection = (event: PromiseRejectionEvent) => {
    this.addLog('error', [
      'Unhandled Promise Rejection:',
      event.reason
    ])
  }

  private formatMessage(args: any[]): string {
    return args
      .map(arg => {
        if (typeof arg === 'string') return arg
        if (arg instanceof Error) return `${arg.name}: ${arg.message}`
        try {
          return JSON.stringify(arg, null, 2)
        } catch {
          return String(arg)
        }
      })
      .join(' ')
  }

  getLogs(): LogEntry[] {
    return [...this.logs]
  }

  clearLogs() {
    this.logs = []
    this.notifyListeners()
  }

  subscribe(listener: (logs: LogEntry[]) => void) {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  private notifyListeners() {
    this.listeners.forEach(listener => listener(this.getLogs()))
  }

  exportLogs(): string {
    return JSON.stringify(this.logs, null, 2)
  }

  downloadLogs() {
    const data = this.exportLogs()
    const blob = new Blob([data], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `logs-${new Date().toISOString()}.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  getStats() {
    const byLevel = this.logs.reduce((acc, log) => {
      acc[log.level] = (acc[log.level] || 0) + 1
      return acc
    }, {} as Record<LogLevel, number>)

    return {
      total: this.logs.length,
      byLevel,
      enabled: this.enabled,
    }
  }
}

// Singleton instance
export const logCollector = new LogCollector()

// Make it accessible from browser console for debugging
if (typeof window !== 'undefined') {
  (window as any).__logCollector = logCollector
}
